import { useCallback, ComponentType, ReactNode } from 'react'
import styled from 'styled-components'
import useEmblaCarousel from 'embla-carousel-react'

import { media, mediaMax } from 'styles/media'

import useBufferedSlideCount from '../../../hooks/useBufferedSlideCount'
import useEmblaNavigation from '../../../hooks/useEmblaNavigation'

import ChevronDownIcon from '../../icons/ChevronDown'

import Slider from './Slider'
import List from './List'
import { Arrow, Inner, Header, Title } from './shared'

const BREAKPOINT = 'lg' as const

const MobileOnly = styled.div`
  ${media[BREAKPOINT]`
    display: none;
  `}
`

const DesktopOnly = styled.div`
  ${mediaMax[BREAKPOINT]`
    display: none;
  `}
`

type PaginatedSlidesProps<T> = {
  customTitle?: ReactNode
  called: boolean
  loading: boolean
  canFetchMore: boolean
  onFetchMore: () => any
  items: T[]
  ItemComponent: ComponentType<{ item: T }>
  loadingPlaceholder?: ReactNode
  minHeight?: string
}

const PaginatedSlides = <T extends Record<'id', string>>({
  customTitle,
  called,
  loading,
  canFetchMore,
  onFetchMore,
  items,
  ItemComponent,
  loadingPlaceholder,
  minHeight,
}: PaginatedSlidesProps<T>) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    slidesToScroll: 3,
    containScroll: 'trimSnaps',
  })

  const onReachEnd = useCallback(() => {
    if (canFetchMore && !loading) onFetchMore()
  }, [canFetchMore, loading, onFetchMore])

  const { handlePrev, handleNext, prevDisabled, nextDisabled, singlePage } =
    useEmblaNavigation(emblaApi, { onReachEnd })

  // With everything on one snap and nothing left to fetch, the arrows can
  // never do anything — drop them rather than render a disabled pair.
  const hideArrows = singlePage && !canFetchMore

  const totalSlides = items?.length || 0
  const slideCount = useBufferedSlideCount(totalSlides, emblaApi)
  const paginationNotReady = !called || loading || !items

  return (
    <Inner $minHeight={paginationNotReady ? minHeight : 'auto'}>
      <Header>
        {customTitle || <Title>More Escapes</Title>}

        {!hideArrows && (
          <DesktopOnly>
            <Arrow
              left
              onClick={handlePrev}
              disabled={paginationNotReady || prevDisabled}
              aria-label="Previous"
            >
              <ChevronDownIcon />
            </Arrow>
            <Arrow
              right
              onClick={handleNext}
              disabled={paginationNotReady || nextDisabled}
              aria-label="Next"
            >
              <ChevronDownIcon />
            </Arrow>
          </DesktopOnly>
        )}
      </Header>

      <DesktopOnly>
        <Slider
          emblaRef={emblaRef}
          items={items || []}
          slideCount={slideCount}
          ItemComponent={ItemComponent}
          loadingPlaceholder={loadingPlaceholder}
        />
      </DesktopOnly>

      <MobileOnly>
        <List
          loading={loading}
          canFetchMore={canFetchMore}
          onFetchMore={onFetchMore}
          items={items || []}
          ItemComponent={ItemComponent}
          loadingPlaceholder={loadingPlaceholder}
        />
      </MobileOnly>
    </Inner>
  )
}

export default PaginatedSlides
